import { IPatchRetailersRequest, IVisibleKeyword, keywordAffiliation, TKeyword, TKeywordTag, TRetailer } from 'types';
import {
    TGetClientKeywordsResponse,
    TGetClientKeywordsV2Response,
    TAddKeywordResponse,
    TUpdateKeywordResponse,
} from 'contracts/keywords';
import { cdmApi } from './index';

const PREFIX = '/keywords';
/* eslint-disable  @typescript-eslint/no-non-null-assertion */

const keywordsApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        getClientKeywords: builder.query<TGetClientKeywordsResponse, void>({
            query: (params) => ({ url: `${PREFIX}?isTyped=false`, method: 'get', params }),
        }),

        getClientKeywordsByType: builder.query<TGetClientKeywordsResponse, { type: keywordAffiliation }>({
            query: (params) => ({ url: `${PREFIX}/`, method: 'get', params }),
        }),

        // V2 version
        getKeywords: builder.query<TGetClientKeywordsV2Response, { type?: keywordAffiliation }>({
            query: (params) => ({ url: `${PREFIX}/v2`, method: 'get', params }),
            providesTags: () => ['Keywords'],
        }),

        getKeywordsByRetailers: builder.query<{ code: number; retailers: TRetailer[] }, any>({
            query: (params) => ({ url: `retailers?keywords=true`, method: 'get', params }),
            providesTags: () => [{ type: 'KeywordsByRetailer', id: 'LIST' }],
        }),

        updateKeywordRetailers: builder.mutation<any, IPatchRetailersRequest>({
            query: (data) => ({ url: `retailers/`, method: 'put', data }),
            invalidatesTags: [{ type: 'KeywordsByRetailer', id: 'LIST' }],
        }),

        exportKeywordsByRetailers: builder.query<ArrayBuffer, { isTemplate?: boolean; withWeights?: boolean }>({
            query: (params) => ({
                url: `retailers/excel/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),

        uploadRetailers: builder.mutation<any, FormData>({
            query: (data) => ({ url: `retailers/excel/upload`, method: 'POST', data, responseType: 'arraybuffer' }),
            invalidatesTags: [{ type: 'KeywordsByRetailer', id: 'LIST' }],
        }),

        exportClientKeywords: builder.query<any, { types: keywordAffiliation[]; isTemplate?: boolean }>({
            query: (params) => ({
                url: `${PREFIX}/excel/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),

        updateClientKeywords: builder.mutation<
            TUpdateKeywordResponse,
            { keyword: TKeyword; type?: keywordAffiliation }
        >({
            query: (data) => ({ url: `${PREFIX}/update`, method: 'put', data: data.keyword }),
            invalidatesTags: () => ['Keywords'],
        }),
        addClientKeyword: builder.mutation<TAddKeywordResponse, IVisibleKeyword>({
            query: (data) => ({ url: `${PREFIX}/add`, method: 'POST', data }),
            invalidatesTags: () => ['Keywords'],
        }),

        deleteClientKeywords: builder.mutation<
            { code: number; nonExisitingIds?: number[] },
            { keywords: { id: number; type?: 'search' | 'banner' }[] }
        >({
            query: (data) => ({ url: `${PREFIX}/delete`, method: 'DELETE', data: data.keywords }),
            invalidatesTags: () => ['Keywords'],
        }),

        uploadClientKeywords: builder.mutation<
            { code: number; created: TKeyword[]; updated: TKeyword[]; notAccepted: TKeyword[] },
            FormData
        >({
            query: (data) => ({ url: `${PREFIX}/excel/upload`, method: 'POST', data }),
            invalidatesTags: () => ['Keywords'],
        }),
        exportNonAcceptedKeywords: builder.mutation<any, TKeyword[]>({
            query: (data) => ({
                url: `${PREFIX}/excel/error`,
                method: 'POST',
                data,
                responseType: 'arraybuffer',
            }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetClientKeywordsQuery,
    useGetKeywordsQuery,
    useGetKeywordsByRetailersQuery,
    useUpdateKeywordRetailersMutation,
    useGetClientKeywordsByTypeQuery,
    useLazyExportClientKeywordsQuery,
    useUpdateClientKeywordsMutation,
    useDeleteClientKeywordsMutation,
    useAddClientKeywordMutation,
    useUploadClientKeywordsMutation,
    useExportNonAcceptedKeywordsMutation,
    useLazyExportKeywordsByRetailersQuery,
    useUploadRetailersMutation,
} = keywordsApi;
